import { useEffect, useState, useCallback, useRef } from "react";

import BurgerButton from "../../ui/BurgerButton";
import IconsList from "../../ui/icon/IconsList";
import Button from "../../ui/Button/Button";

import { useBurgerMenu } from "../../../hooks/useBurgerMenu";

import s from "./BurgerController.module.scss";

function BurgerController({
  variant = "left", // "left" | "right" | "below"
  title = "Меню",
  lockScroll = true,
  buttonClassName = "",
  panelClassName = "",
  overlayClassName = "",
  children,
}) {
  const { open, toggleMenu, closeMenu } = useBurgerMenu({ closeOnEsc: true });

  // refs
  const rootRef = useRef(null);
  const panelRef = useRef(null);

  // ✅ фокус-рефы
  const burgerBtnRef = useRef(null);
  const closeBtnRef = useRef(null);

  // токен сброса подменю
  const [resetToken, setResetToken] = useState(0);

  // ✅ единая функция закрытия + сброс подменю + возврат фокуса
  const closeAndReset = useCallback(() => {
    // ВАЖНО: уводим фокус из панели ДО aria-hidden
    burgerBtnRef.current?.focus();

    closeMenu();
    setResetToken((t) => t + 1);
  }, [closeMenu]);

  // ✅ при открытии — фокус на крестик
  useEffect(() => {
    if (open) {
      closeBtnRef.current?.focus();
    }
  }, [open]);

  // ✅ Закрытие по клику вне панели
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e) => {
      const panel = panelRef.current;
      const root = rootRef.current;
      if (!panel || !root) return;

      // клик внутри панели — не закрываем
      if (panel.contains(e.target)) return;

      // клик по кнопке бургера — не закрываем (toggle сам разрулит)
      const burgerBtnWrap = root.querySelector('[data-burger-btn="true"]');
      if (burgerBtnWrap && burgerBtnWrap.contains(e.target)) return;

      closeAndReset();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, closeAndReset]);

  // ✅ Блокируем скролл страницы при открытом drawer
  useEffect(() => {
    if (!lockScroll) return;
    if (!open) return;

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, lockScroll]);

  const panelVariantClass =
    variant === "right"
      ? s.panelRight
      : variant === "below"
      ? s.panelBelow
      : s.panelLeft;

  return (
    <div className={s.root} ref={rootRef}>
      <span data-burger-btn="true">
        <BurgerButton
          ref={burgerBtnRef}
          isOpen={open}
          onClick={toggleMenu}
          className={buttonClassName}
        />
      </span>

      {open && (
        <button
          type="button"
          className={`${s.overlay} ${overlayClassName}`}
          aria-label="Закрыть меню"
          onClick={closeAndReset}
        />
      )}

      <aside
        ref={panelRef}
        className={`${s.panel} ${panelVariantClass} ${open ? s.panelOpen : ""} ${panelClassName}`}
        role="dialog"
        aria-label={title}
        aria-hidden={!open}
      >
        <div className={s.panelHeader}>
          <div className={s.panelTitle}>{title}</div>
          <button
            ref={closeBtnRef}
            type="button"
            className={s.closeBtn}
            onClick={closeAndReset}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className={s.containerPanelBody}>
          <div className={s.panelBody} key={resetToken}>
            {typeof children === "function"
              ? children({ open, closeMenu: closeAndReset, resetToken })
              : children}
          </div>

          <div className={s.panelContact}>
            <div className={s.social}>
              <IconsList preset="header" />
            </div>

            <div className={s.number}>
              <a href="tel:89770906036">
                <span>Тел:</span>
                <span>+7 (977) 090-60-36</span>
              </a>
            </div>

            <div className={s.containerBtn}>
              <Button
                type="button"
                onClick={() => {
                  closeAndReset();
                  setTimeout(() => {
                    document
                      .querySelector("#order-form")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });

                    setTimeout(() => {
                      document.querySelector('input[name="phone"]')?.focus();
                    }, 500);
                  }, 50);
                }}
              >
                Заказать
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default BurgerController;
